import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CardContent, Typography, Box, Chip, Avatar, Divider, useTheme, useMediaQuery, CardActionArea } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GlassCard } from '../../../components/ui';
import Person from '@mui/icons-material/Person';
import type { WikiNpcItem } from '../../../types';
import { ACCENT_COLORS, BG_COLORS, TEXT_COLORS, WIKI_COLORS } from '../../../utils/colors';
import { WIKI_CARD } from '../../../utils/responsive';

interface WikiNpcCardProps {
  npc: WikiNpcItem;
  source: string;
  wikiSubCategory?: number;
  onPreview?: (itemId: number, detailUrl: string) => void;
  /** Карточка открыта в боковой панели (десктоп) — подсвечиваем, как выбранную строку в поиске вики. */
  selected?: boolean;
}

// Helper functions for NPCs
const formatNumber = (num: number | undefined): string => {
  if (!num || num === 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};


const WikiNpcCard: React.FC<WikiNpcCardProps> = ({
  npc,
  source,
  wikiSubCategory,
  onPreview,
  selected = false
}) => {
  const { t } = useTranslation('wiki');
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const iconSize = isMd ? WIKI_CARD.iconSize.md : isSm ? WIKI_CARD.iconSize.sm : WIKI_CARD.iconSize.xs;

  // Build RESTful URL for NPC detail page
  const getNpcPath = () => {
    let path = `/wiki/${source}`;
    if (wikiSubCategory) {
      path += `/subcategories/${wikiSubCategory}`;
    }
    path += `/details/${npc.id}`;
    return path;
  };

  return (
    <GlassCard
      sx={{
        height: '100%',
        ...(selected && { borderColor: ACCENT_COLORS.PRIMARY, background: BG_COLORS.GLASS_LIGHTER }),
        '&:hover': {
          transform: 'translateY(-4px) translateZ(0)',
          borderColor: selected ? ACCENT_COLORS.PRIMARY : 'rgba(185, 28, 28, 0.6)',
          background: BG_COLORS.GLASS_LIGHTER,
          boxShadow: 'none',
        },
      }}
    >
      <CardActionArea
        {...(onPreview
          ? { onClick: () => onPreview(npc.id, getNpcPath()) }
          : { component: RouterLink, to: getNpcPath() }
        )}
        sx={{
          height: '100%',
          '& .MuiCardActionArea-focusHighlight': {
            background: 'transparent',
          },
        }}
      >
        <CardContent sx={{ textAlign: 'center', p: WIKI_CARD.padding }}>
        {/* NPC Avatar */}
        <Box sx={{ mb: WIKI_CARD.iconMargin, display: 'flex', justifyContent: 'center' }}>
          <Avatar
            sx={{
              width: iconSize,
              height: iconSize,
              bgcolor: BG_COLORS.GLASS_LIGHT,
              color: TEXT_COLORS.SECONDARY,
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            }}
          >
            <Person sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
          </Avatar>
        </Box>

        {/* NPC Name */}
        <Typography
          variant="subtitle1"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: { xs: 0.5, sm: 1 },
            fontSize: WIKI_CARD.titleFontSize,
            lineHeight: 1.3,
            minHeight: WIKI_CARD.titleMinHeight,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {npc.name}
        </Typography>

        {/* NPC Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: WIKI_CARD.gap, mt: { xs: 0.5, sm: 1 } }}>
          {/* Race Chip */}
          <Chip
            label={npc.race}
            size="small"
            sx={{
              backgroundColor: BG_COLORS.GLASS_LIGHT,
              color: TEXT_COLORS.SECONDARY,
              fontWeight: 500,
              fontSize: WIKI_CARD.chipFontSize,
              height: WIKI_CARD.chipHeight,
            }}
          />

          {/* Aggressive Status */}
          {npc.isAggressive && (
            <Chip
              label={t('npcProperties.aggressive')}
              size="small"
              sx={{
                backgroundColor: WIKI_COLORS.RED.background,
                color: WIKI_COLORS.RED.text,
                fontWeight: 600,
                fontSize: WIKI_CARD.chipFontSize,
                height: WIKI_CARD.chipHeight,
              }}
            />
          )}

          {/* Category */}
          {npc.wikiCategory && npc.wikiCategory !== 'NONE' && (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: WIKI_CARD.detailsFontSize }}>
              {npc.wikiCategory}
            </Typography>
          )}

          <Divider sx={{ my: WIKI_CARD.gap, opacity: 0.6 }} />

          {/* NPC Stats Grid */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.15, sm: 0.25 } }}>
            {/* Level */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_CARD.detailsFontSize }}>
                {t('npcProperties.level')}:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: WIKI_CARD.detailsFontSize,
                  fontWeight: 600,
                  color: TEXT_COLORS.PRIMARY
                }}
              >
                {npc.lvl}
              </Typography>
            </Box>

            {/* HP */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_CARD.detailsFontSize }}>
                HP:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: WIKI_CARD.detailsFontSize,
                  fontWeight: 600,
                  color: TEXT_COLORS.PRIMARY
                }}
              >
                {formatNumber(npc.hp)}
              </Typography>
            </Box>

            {/* Physical Attack */}
            {npc.physicalAttack && npc.physicalAttack > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_CARD.detailsFontSize }}>
                  {t('npcProperties.physicalAttack')}:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: WIKI_CARD.detailsFontSize,
                    fontWeight: 600,
                    color: TEXT_COLORS.PRIMARY
                  }}
                >
                  {formatNumber(npc.physicalAttack)}
                </Typography>
              </Box>
            )}

            {/* Magical Attack */}
            {npc.magicalAttack && npc.magicalAttack > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_CARD.detailsFontSize }}>
                  {t('npcProperties.magicalAttack')}:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: WIKI_CARD.detailsFontSize,
                    fontWeight: 600,
                    color: TEXT_COLORS.PRIMARY
                  }}
                >
                  {formatNumber(npc.magicalAttack)}
                </Typography>
              </Box>
            )}

            {/* EXP/SP */}
            {((npc.exp && npc.exp > 0) || (npc.sp && npc.sp > 0)) && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_CARD.detailsFontSize }}>
                  {t('npcProperties.exp')}:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: WIKI_CARD.detailsFontSize,
                    fontWeight: 600,
                    color: TEXT_COLORS.PRIMARY
                  }}
                >
                  {formatNumber(npc.exp)} exp
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
      </CardActionArea>
    </GlassCard>
  );
};

export default WikiNpcCard;